import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { selectUserId, selectTeamIds, selectTeams, selectTeamName, selectTier, selectSportsClubName, selectSportsClubId } from '../store/userSlice';
import { useAuth } from '../context/AuthContext';
import { TeamControllerService, PreseasonGameControllerService, TermControllerService } from '../api';
import { toast } from 'react-toastify';
import './SportsClubReceivedMatches.css';

function SportsClubReceivedMatches() {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeamId, setSelectedTeamId] = useState('all');
  const [selectedScheduleId, setSelectedScheduleId] = useState('all');
  const [availableDateRanges, setAvailableDateRanges] = useState([]);
  const [isLoadingDateRanges, setIsLoadingDateRanges] = useState(false);
  const pendingScheduleIdRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);
  const [pitchConfirmModal, setPitchConfirmModal] = useState({ isOpen: false, match: null });


  const userId = useSelector(selectUserId);
  const teamIds = useSelector(selectTeamIds);
  const teamId = teamIds?.[0];
  const reduxTeams = useSelector(selectTeams);
  const teamName = useSelector(selectTeamName);
  const tier = useSelector(selectTier);
  const sportsClubName = useSelector(selectSportsClubName);
  const sportsClubId = useSelector(selectSportsClubId);
  const { hasRole } = useAuth();
  const isCoach = hasRole('COACH');
  const isSportsClub = hasRole('SPORTS_CLUB');
  const navigate = useNavigate();
  const location = useLocation();

  // Odczytaj parametry z przekierowania (z /sparingi lub /preseason-games-summary)
  useEffect(() => {
    // Jeśli przekazano teamId, ustaw go jako wybrany
    if (location.state?.teamId) {
      setSelectedTeamId(location.state.teamId.toString());
    }
    // Zapisz pending scheduleId do ustawienia po załadowaniu zakresów dat
    if (location.state?.termId) {
      pendingScheduleIdRef.current = location.state.termId;
    }
    // Wyczyść state po odczytaniu
    if (location.state?.teamId || location.state?.termId) {
      navigate('/sports-club-received-matches', { replace: true, state: {} });
    }
  }, [location.state, navigate, isSportsClub]);

  // Pagination state
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const getStatusClass = (status) => {
    const statusClasses = {
      'PENDING': 'status-oczekuje',
      'PENDING_DATE_TIME_PROPOSAL': 'status-oczekuje',
      'PENDING_CHANGE_DATE': 'status-oczekuje',
      'CONFIRMED': 'status-zaakceptowano',
      'BLOCKED': 'status-odrzucono',
      'CANCELLED': 'status-odrzucono'
    };
    return statusClasses[status] || 'status-nieznany';
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      'PENDING': 'Oczekuje',
      'PENDING_DATE_TIME_PROPOSAL': 'Propozycja terminu',
      'PENDING_CHANGE_DATE': 'Propozycja zmiany terminu',
      'CONFIRMED': 'Potwierdzono',
      'BLOCKED': 'Odrzucono',
      'CANCELLED': 'Anulowano'
    };
    return statusLabels[status] || status;
  };

  // Fetch teams
  useEffect(() => {
    const fetchTeams = async () => {
      if (isCoach) {
        // Dla COACH: mapuj drużyny z Redux do lokalnego formatu
        const mappedTeams = (reduxTeams || []).map(t => ({ teamId: t.id, name: t.teamName }));
        setTeams(mappedTeams);
        // Ustaw pierwszą drużynę jeśli nie wybrano jeszcze konkretnej
        if (selectedTeamId === 'all' && mappedTeams.length > 0) {
          setSelectedTeamId(mappedTeams[0].teamId.toString());
        }
        return;
      }

      if (!sportsClubId) return;

      try {
        const teamsData = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
        setTeams(teamsData || []);
      } catch (error) {
        // error handled silently
      }
    };

    fetchTeams();
  }, [sportsClubId, isCoach, reduxTeams]);

  // Pobierz zakresy dat dla wybranej drużyny
  useEffect(() => {
    const fetchDateRanges = async (teamIdToFetch) => {
      setIsLoadingDateRanges(true);
      try {
        const dateRanges = await TermControllerService.getAllDatesForSchedule(
          Number(teamIdToFetch)
        );
        setAvailableDateRanges(dateRanges || []);
      } catch (error) {
        setAvailableDateRanges([]);
      } finally {
        setIsLoadingDateRanges(false);
      }
    };

    // Pobierz zakresy dat dla wybranej drużyny (nie dla 'all')
    if (selectedTeamId && selectedTeamId !== 'all') {
      fetchDateRanges(selectedTeamId);
    }
    // Dla SPORTS_CLUB z 'all' - wyczyść zakresy
    else if (isSportsClub && selectedTeamId === 'all') {
      setAvailableDateRanges([]);
      setSelectedScheduleId('all');
    }
  }, [isCoach, isSportsClub, teamId, selectedTeamId]);

  // Ustaw wybrany zakres dat gdy availableDateRanges są załadowane i jest pendingScheduleId
  useEffect(() => {
    if (pendingScheduleIdRef.current && availableDateRanges.length > 0) {
      const pendingId = Number(pendingScheduleIdRef.current);
      const found = availableDateRanges.some(r => r.id === pendingId);
      if (found) {
        setSelectedScheduleId(pendingId.toString());
      }
      pendingScheduleIdRef.current = null;
    }
  }, [availableDateRanges]);

  // Reset page when filter changes
  useEffect(() => {
    setPage(0);
  }, [selectedTeamId]);

  // Fetch matches when selectedTeamId or page changes
  useEffect(() => {
    const fetchMatches = async () => {
      // Sprawdź czy mamy wybraną drużynę
      if ((isCoach && (!selectedTeamId || selectedTeamId === 'all')) || (isSportsClub && !userId)) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const pageable = {
          page: page,
          size: size,
        };

        let response;
        if (isCoach) {
          // Dla COACH pobierz zapytania dla wybranej drużyny
          response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(parseInt(selectedTeamId), pageable);
        } else if (isSportsClub) {
          if (selectedTeamId === 'all') {
            // Pobierz wszystkie zapytania dla klubu sportowego
            response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubReceiver(userId, pageable);
          } else {
            // Pobierz zapytania dla konkretnej drużyny
            response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(parseInt(selectedTeamId), pageable);
          }
        }

        setMatches(response?.content || []);
        setTotalPages(response?.totalPages || 0);
        setTotalElements(response?.totalElements || 0);
      } catch (error) {
        toast.error('Nie udało się pobrać zapytań');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [userId, teamId, selectedTeamId, page, size, isCoach, isSportsClub]);

  const openModal = (match, e) => {
    e.stopPropagation();
    setSelectedMatch(match);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMatch(null);
    setReplyMessage('');
  };

  const handleSendReply = async () => {
    if (!selectedMatch || !replyMessage.trim()) return;

    try {
      setSendingReply(true);

      // Określ senderId i receiverId w zależności od kontekstu
      // W SportsClubReceivedMatches - zalogowany użytkownik jest opponentem (odbiorcą zapytania)
      const senderId = selectedMatch.teamOpponentId;
      const receiverId = selectedMatch.teamCreatorId;

      await PreseasonGameControllerService.addMessage({
        preseasonGameId: selectedMatch.id,
        senderId: senderId,
        receiverId: receiverId,
        senderUserId: userId,
        message: replyMessage.trim()
      });

      toast.success('Wiadomość została wysłana');
      setReplyMessage('');

      // Odśwież dane matcha, aby pokazać nową wiadomość
      const pageable = { page, size };
      let response;

      if (isCoach) {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(teamId, pageable);
      } else if (isSportsClub) {
        if (selectedTeamId === 'all') {
          response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubReceiver(userId, pageable);
        } else {
          response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(parseInt(selectedTeamId), pageable);
        }
      }

      setMatches(response?.content || []);

      // Zaktualizuj selectedMatch z nowymi danymi
      const updatedMatch = response?.content?.find(m => m.id === selectedMatch.id);
      if (updatedMatch) {
        setSelectedMatch(updatedMatch);
      }
    } catch (error) {
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSendingReply(false);
    }
  };

  const handleConfirmTime = async (match, e) => {
    e.stopPropagation();
    try {
      // Format time to HH:mm
      const formatTime = (time) => time ? time.substring(0, 5) : null;

      await PreseasonGameControllerService.approvePreseasonGame(match.id, match.teamOpponentId);
      toast.success('Godzina meczu została potwierdzona');

      // Odśwież dane
      const pageable = { page, size };
      let response;

      if (isCoach) {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(teamId, pageable);
      } else if (isSportsClub) {
        if (selectedTeamId === 'all') {
          response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubReceiver(userId, pageable);
        } else {
          response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(parseInt(selectedTeamId), pageable);
        }
      }

      setMatches(response?.content || []);
      setTotalPages(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);
    } catch (error) {
      if (error?.body?.error === 'SportsClubNotApproved') {
        toast.error(error.body.message);
      } else {
        toast.error('Nie udało się potwierdzić godziny');
      }
    }
  };

  const handleApprove = async () => {
    if (!selectedMatch) return;

    const msg = replyMessage.trim() || undefined;

    if (!selectedMatch.hasPitchAvailable) {
      if (selectedMatch.changedDate && selectedMatch.changedStartTime && selectedMatch.changedEndTime) {
        await executeApprove(selectedMatch.id, true, msg);
        return;
      }
      if (!selectedMatch.date) {
        await executeApprove(selectedMatch.id, false, msg);
        return;
      }
      if (selectedMatch.hasOtherGamesInSchedule) {
        setPitchConfirmModal({ isOpen: true, match: selectedMatch, message: msg });
        return;
      }
      await executeApprove(selectedMatch.id, false, msg);
      return;
    }

    await executeApprove(selectedMatch.id, true, msg);
  };

  const handleApproveWithCalendarDecision = async (shouldBlockCalendar) => {
    const match = pitchConfirmModal.match;
    const msg = pitchConfirmModal.message;
    setPitchConfirmModal({ isOpen: false, match: null, message: undefined });
    if (!match) return;
    await executeApprove(match.id, shouldBlockCalendar, msg);
  };

  const executeApprove = async (matchId, shouldBlockCalendar, message) => {
    try {
      setModalLoading(true);
      await PreseasonGameControllerService.approvePreseasonGame(matchId, selectedMatch.teamOpponentId, shouldBlockCalendar, message);
      toast.success('Zapytanie zaakceptowane pomyślnie');
      closeModal();
      // Odśwież dane
      const pageable = {
        page: page,
        size: size,
      };

      let response;
      if (selectedTeamId === 'all') {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubReceiver(userId, pageable);
      } else {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(parseInt(selectedTeamId), pageable);
      }
      setMatches(response?.content || []);
      setTotalPages(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);
    } catch (error) {
      if (error?.body?.error === 'SportsClubNotApproved') {
        toast.error(error.body.message);
      } else if (error?.status === 409 && error?.body?.message) {
        toast.error(error.body.message);
      } else {
        toast.error('Nie udało się zaakceptować zapytania');
      }
    } finally {
      setModalLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedMatch) return;

    const msg = replyMessage.trim() || undefined;

    try {
      setModalLoading(true);
      await PreseasonGameControllerService.rejectPreseasonGame(selectedMatch.id, selectedMatch.teamOpponentId, msg);
      toast.success('Zapytanie odrzucone pomyślnie');
      closeModal();
      // Odśwież dane
      const pageable = {
        page: page,
        size: size,
      };

      let response;
      if (selectedTeamId === 'all') {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesBySportsClubReceiver(userId, pageable);
      } else {
        response = await PreseasonGameControllerService.getPendingPreseasonGamesByTeamReceiver(parseInt(selectedTeamId), pageable);
      }
      setMatches(response?.content || []);
      setTotalPages(response?.totalPages || 0);
      setTotalElements(response?.totalElements || 0);
    } catch (error) {
      if (error?.status === 409 && error?.body?.message) {
        toast.error(error.body.message);
      } else {
        toast.error('Nie udało się odrzucić zapytania');
      }
    } finally {
      setModalLoading(false);
    }
  };


  // Pobierz zakres dat dla wybranego scheduleId
  const getSelectedDateRange = () => {
    if (selectedScheduleId === 'all' || !availableDateRanges.length) {
      return null;
    }
    return availableDateRanges.find(r => r.id?.toString() === selectedScheduleId);
  };

  const selectedDateRange = getSelectedDateRange();

  // Filtruj matches po wybranym zakresie dat
  const filteredMatches = matches.filter(match => {
    if (!selectedDateRange) return true; // Jeśli brak wybranego zakresu, pokazuj wszystkie
    if (!match.date) {
      // Mecz bez daty — filtruj po schedule opponenta (bo tu jesteśmy odbiorcą)
      const scheduleStart = match.opponentScheduleStartDate;
      const scheduleEnd = match.opponentScheduleEndDate;
      if (!scheduleStart && !scheduleEnd) return true; // brak info o schedule — pokazuj
      if (selectedDateRange.startDate && scheduleEnd && scheduleEnd < selectedDateRange.startDate) return false;
      if (selectedDateRange.endDate && scheduleStart && scheduleStart > selectedDateRange.endDate) return false;
      return true;
    }
    const matchDate = match.date;
    if (selectedDateRange.startDate && matchDate < selectedDateRange.startDate) return false;
    if (selectedDateRange.endDate && matchDate > selectedDateRange.endDate) return false;
    return true;
  });

  // Grupuj matche według drużyny otrzymującej (naszej drużyny) gdy wybrano 'all'
  const groupedMatches = () => {
    if (selectedTeamId === 'all') {
      const groups = {};
      filteredMatches.forEach(match => {
        const teamName = match.teamOpponentName || `Drużyna #${match.teamOpponentId}`;
        if (!groups[teamName]) {
          groups[teamName] = [];
        }
        groups[teamName].push(match);
      });
      return groups;
    }
    return null;
  };

  const matchGroups = groupedMatches();

  if (loading) {
    return (
      <div className="sports-club-received-matches-container">
        <div className="loading">Ładowanie zapytań...</div>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="sports-club-received-matches-container">
        <div className="no-matches">Musisz być zalogowany, aby zobaczyć swoje zapytania.</div>
      </div>
    );
  }

  return (
    <div className="sports-club-received-matches-container">
      <h2>Otrzymane zapytania</h2>

      {/* Mobile: dropdown drużyny */}
      {teams.length > 0 && (
        <div className="mobile-team-select-wrapper">
          <select
            className="mobile-team-select"
            value={selectedTeamId}
            onChange={(e) => setSelectedTeamId(e.target.value)}
          >
            {isSportsClub && <option value="all">Wszystkie drużyny</option>}
            {teams.map(team => (
              <option key={team.teamId} value={team.teamId}>
                {team.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Mobile: nawigator zakresów dat */}
      <div className="mobile-date-nav">
        <button
          className="mobile-date-nav-arrow"
          disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || selectedScheduleId === 'all'}
          onClick={() => {
            const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
            if (idx <= 0) {
              setSelectedScheduleId('all');
            } else {
              setSelectedScheduleId(availableDateRanges[idx - 1].id.toString());
            }
          }}
        >
          &#8592;
        </button>
        <span className="mobile-date-nav-label">
          {selectedTeamId === 'all' || isLoadingDateRanges
            ? 'Wszystkie daty'
            : selectedScheduleId === 'all'
              ? 'Wszystkie daty'
              : (() => {
                  const range = availableDateRanges.find(r => r.id.toString() === selectedScheduleId.toString());
                  return range ? `${range.startDate} - ${range.endDate}` : 'Wszystkie daty';
                })()
          }
        </span>
        <button
          className="mobile-date-nav-arrow"
          disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || (selectedScheduleId !== 'all' && availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString()) >= availableDateRanges.length - 1)}
          onClick={() => {
            if (selectedScheduleId === 'all') {
              if (availableDateRanges.length > 0) setSelectedScheduleId(availableDateRanges[0].id.toString());
            } else {
              const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
              if (idx < availableDateRanges.length - 1) setSelectedScheduleId(availableDateRanges[idx + 1].id.toString());
            }
          }}
        >
          &#8594;
        </button>
      </div>

      <div className="mobile-filters-divider"></div>

      {/* Desktop: dropdown drużyny + nawigator dat */}
      <div className="desktop-filters">
        {teams.length > 0 && (
          <div className="desktop-team-select-wrapper">
            <select
              className="desktop-team-select"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              {isSportsClub && <option value="all">Wszystkie drużyny</option>}
              {teams.map(team => (
                <option key={team.teamId} value={team.teamId}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="desktop-date-nav">
          <button
            className="desktop-date-nav-arrow"
            disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || selectedScheduleId === 'all'}
            onClick={() => {
              const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
              if (idx <= 0) {
                setSelectedScheduleId('all');
              } else {
                setSelectedScheduleId(availableDateRanges[idx - 1].id.toString());
              }
            }}
          >
            &#8592;
          </button>
          <span className="desktop-date-nav-label">
            {selectedTeamId === 'all' || isLoadingDateRanges
              ? 'Wszystkie daty'
              : selectedScheduleId === 'all'
                ? 'Wszystkie daty'
                : (() => {
                    const range = availableDateRanges.find(r => r.id.toString() === selectedScheduleId.toString());
                    return range ? `${range.startDate} - ${range.endDate}` : 'Wszystkie daty';
                  })()
            }
          </span>
          <button
            className="desktop-date-nav-arrow"
            disabled={selectedTeamId === 'all' || availableDateRanges.length === 0 || (selectedScheduleId !== 'all' && availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString()) >= availableDateRanges.length - 1)}
            onClick={() => {
              if (selectedScheduleId === 'all') {
                if (availableDateRanges.length > 0) setSelectedScheduleId(availableDateRanges[0].id.toString());
              } else {
                const idx = availableDateRanges.findIndex(r => r.id.toString() === selectedScheduleId.toString());
                if (idx < availableDateRanges.length - 1) setSelectedScheduleId(availableDateRanges[idx + 1].id.toString());
              }
            }}
          >
            &#8594;
          </button>
        </div>
      </div>

      {filteredMatches.length === 0 ? (
        <div className="no-matches">
          {matches.length === 0 ? 'Nie masz jeszcze żadnych zapytań o sparingi.' : 'Brak zapytań dla wybranych filtrów.'}
        </div>
      ) : matchGroups ? (
        // Wyświetl zgrupowane matche (gdy wybrano 'all')
        <div className="matches-grouped">
          {Object.entries(matchGroups).map(([teamName, teamMatches]) => (
            <div key={teamName} className="team-group">
              <h3 className="team-group-header">{teamName}</h3>
              <div className="matches-list">
                {teamMatches.map((match) => {
                  return (
                    <div key={match.id} className="match-item">
                      <div className="match-item-row">
                        {match.creatorLogoUrl && (
                          <img src={match.creatorLogoUrl} alt="" className="match-item-logo" />
                        )}
                        <div className="match-item-content">
                      <div className="match-header">
                        <div className="match-header-row">
                          <div className="match-team-info">
                            <h3 className="match-club-name">
                              {match.teamCreatorName || 'Nieznana drużyna'}
                            </h3>
                            {match.teamOpponentTier && (
                              <span className="match-team-tier">{match.teamOpponentTier}</span>
                            )}
                          </div>
                          <div className="match-right-section">
                            <button
                              className="respond-button"
                              onClick={(e) => openModal(match, e)}
                            >
                              Odpowiedz
                            </button>
                            <span className={`status-badge ${getStatusClass(match.status)}`}>
                              {getStatusLabel(match.status)}
                            </span>
                          </div>
                        </div>
                        <div className="match-second-row">
                          <div className="match-location">
                            {match.opponentScheduleStartDate && match.opponentScheduleEndDate && (
                              <span className="match-term-info">Termin: {new Date(match.opponentScheduleStartDate).toLocaleDateString('pl-PL')} - {new Date(match.opponentScheduleEndDate).toLocaleDateString('pl-PL')}</span>
                            )}
                            <span>{match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' || (match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime) ? 'Obecna data' : 'Data'}: {match.date || 'do ustalenia'}</span>
                            {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                              <span>Obecna godzina: {match.startTime} - {match.endTime}</span>
                            )}
                            {match.pendingStatus === 'PENDING_WITHOUT_TIME' && (
                              <span>Godzina: do ustalenia</span>
                            )}
                            {match.pendingStatus !== 'PENDING_WITH_DATE_TIME_CHANGE' && match.pendingStatus !== 'PENDING_WITHOUT_TIME' && (
                              <span>Godzina: {match.startTime || 'Brak'} - {match.endTime || 'Brak'}</span>
                            )}
                            <span>Boisko: {match.hasPitchAvailable ? (match.location || 'do ustalenia') : 'do ustalenia'}</span>
                          </div>
                        </div>
                        {/* Propozycja zmiany terminu - nowa linia */}
                        {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                          <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamOpponentId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                            <div className="time-proposal-header">
                              {match.changeRequesterTeamId === match.teamOpponentId
                                ? '→ Wysłano propozycję zmiany terminu'
                                : '← Otrzymano propozycję zmiany terminu'}
                            </div>
                            <div className="time-proposal-details">
                              <span className="time-proposal-detail-item">📅 {match.changedDate}</span>
                              <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                              {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                            </div>
                          </div>
                        )}
                        {/* Propozycja godziny dla PENDING_WITHOUT_TIME */}
                        {match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime && match.changedEndTime && (
                          <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamOpponentId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                            <div className="time-proposal-header">
                              {match.changeRequesterTeamId === match.teamOpponentId
                                ? '→ Wysłano propozycję terminu'
                                : '← Otrzymano propozycję terminu'}
                            </div>
                            <div className="time-proposal-details">
                              {match.changedDate && <span className="time-proposal-detail-item">📅 {match.changedDate}</span>}
                              <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                              {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                            </div>
                          </div>
                        )}
                      </div>

                        </div>
                      </div>

                      {/* Wiadomości w kafelku */}
                      {match.messages && match.messages.length > 0 && (
                        <div className="card-messages-section">
                          <div className="card-messages-header">Ostatnia wiadomość:</div>
                          <div className="card-message-text">{match.messages[match.messages.length - 1].message}</div>
                          {match.messages[match.messages.length - 1].createdAt && (
                            <div className="card-message-timestamp">
                              {new Date(match.messages[match.messages.length - 1].createdAt + (match.messages[match.messages.length - 1].createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Wyświetl matche bez grupowania (gdy wybrano konkretną drużynę)
        <div className="matches-list">
          {filteredMatches.map((match) => {
            return (
              <div key={match.id} className="match-item">
                <div className="match-item-row">
                  {match.creatorLogoUrl && (
                    <img src={match.creatorLogoUrl} alt="" className="match-item-logo" />
                  )}
                  <div className="match-item-content">
                <div className="match-header">
                  <div className="match-header-row">
                    <div className="match-team-info">
                      <h3 className="match-club-name">
                        {match.teamCreatorName || 'Nieznana drużyna'}
                      </h3>
                      {match.teamOpponentTier && (
                        <span className="match-team-tier">{match.teamOpponentTier}</span>
                      )}
                    </div>
                    <div className="match-right-section">
                      <button
                        className="respond-button"
                        onClick={(e) => openModal(match, e)}
                      >
                        Odpowiedz
                      </button>
                      <span className={`status-badge ${getStatusClass(match.status)}`}>
                        {getStatusLabel(match.status)}
                      </span>
                    </div>
                  </div>
                  <div className="match-second-row">
                    <div className="match-location">
                      <span>{match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' || (match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime) ? 'Obecna data' : 'Data'}: {match.date || 'do ustalenia'}</span>
                      {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                        <span>Obecna godzina: {match.startTime} - {match.endTime}</span>
                      )}
                      {match.pendingStatus === 'PENDING_WITHOUT_TIME' && (
                        <span>Godzina: do ustalenia</span>
                      )}
                      {match.pendingStatus !== 'PENDING_WITH_DATE_TIME_CHANGE' && match.pendingStatus !== 'PENDING_WITHOUT_TIME' && (
                        <span>Godzina: {match.startTime || 'Brak'} - {match.endTime || 'Brak'}</span>
                      )}
                      <span>Boisko: {match.hasPitchAvailable ? (match.location || 'do ustalenia') : 'do ustalenia'}</span>
                    </div>
                  </div>
                  {/* Propozycja zmiany terminu - nowa linia */}
                  {match.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && (
                    <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamOpponentId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                      <div className="time-proposal-header">
                        {match.changeRequesterTeamId === match.teamOpponentId
                          ? '→ Wysłano propozycję zmiany terminu'
                          : '← Otrzymano propozycję zmiany terminu'}
                      </div>
                      <div className="time-proposal-details">
                        <span className="time-proposal-detail-item">📅 {match.changedDate}</span>
                        <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                        {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                      </div>
                    </div>
                  )}
                  {/* Propozycja godziny dla PENDING_WITHOUT_TIME */}
                  {match.pendingStatus === 'PENDING_WITHOUT_TIME' && match.changeRequesterTeamId && match.changedStartTime && match.changedEndTime && (
                    <div className={`time-proposal-row ${match.changeRequesterTeamId === match.teamOpponentId ? 'time-proposal-row--sent' : 'time-proposal-row--received'}`}>
                      <div className="time-proposal-header">
                        {match.changeRequesterTeamId === match.teamOpponentId
                          ? '→ Wysłano propozycję terminu'
                          : '← Otrzymano propozycję terminu'}
                      </div>
                      <div className="time-proposal-details">
                        {match.changedDate && <span className="time-proposal-detail-item">📅 {match.changedDate}</span>}
                        <span className="time-proposal-detail-item">⏰ {match.changedStartTime} - {match.changedEndTime}</span>
                        {match.changedLocation && <span className="time-proposal-detail-item">📍 {match.changedLocation}</span>}
                      </div>
                    </div>
                  )}
                </div>

                  </div>
                </div>

                {/* Wiadomości w kafelku */}
                {match.messages && match.messages.length > 0 && (
                  <div className="card-messages-section">
                    <div className="card-messages-header">Ostatnia wiadomość:</div>
                    <div className="card-message-text">{match.messages[match.messages.length - 1].message}</div>
                    {match.messages[match.messages.length - 1].createdAt && (
                      <div className="card-message-timestamp">
                        {new Date(match.messages[match.messages.length - 1].createdAt + (match.messages[match.messages.length - 1].createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {matches.length > 0 && totalPages > 1 && (
        <div className="pagination-controls">
          <button
            className="pagination-button"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Poprzednia
          </button>
          <span className="pagination-info">
            Strona {page + 1} z {totalPages} (Łącznie: {totalElements} zapytań)
          </span>
          <button
            className="pagination-button"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
          >
            Następna
          </button>
        </div>
      )}

      {/* Modal */}
      {modalOpen && selectedMatch && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Podgląd zapytania</h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              {/* Szczegóły zapytania o sparing */}
              <div className="match-details-section">
                <h4>{selectedMatch.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' ? 'Zapytanie o zmianę terminu' : 'Zapytanie o sparing'}</h4>

                <div className="details-teams-row">
                  <div className="details-team-column">
                    {selectedMatch.opponentLogoUrl && (
                      <div className="details-team-logo">
                        <img src={selectedMatch.opponentLogoUrl} alt="Logo" />
                      </div>
                    )}
                    <p className="details-team-name">{selectedMatch.teamOpponentName || 'Brak danych'}</p>
                    {selectedMatch.teamOpponentTier && (
                      <p className="details-team-tier">{selectedMatch.teamOpponentTier}</p>
                    )}
                  </div>

                  <div className="details-team-column">
                    {selectedMatch.creatorLogoUrl && (
                      <div className="details-team-logo">
                        <img src={selectedMatch.creatorLogoUrl} alt="Logo" />
                      </div>
                    )}
                    <p className="details-team-name">{selectedMatch.teamCreatorName || 'Brak danych'}</p>
                    {selectedMatch.teamCreatorTier && (
                      <p className="details-team-tier">{selectedMatch.teamCreatorTier}</p>
                    )}
                  </div>
                </div>

                <h5 className="details-team-title" style={{ marginTop: '1rem' }}>
                  {selectedMatch.pendingStatus === 'PENDING_WITHOUT_TIME' ? 'Proponowany termin' : 'Termin'}
                </h5>
                <div className="details-grid">
                  {selectedMatch.opponentScheduleStartDate && selectedMatch.opponentScheduleEndDate && (
                    <div className="detail-item">
                      <span className="detail-label">Propozycja na termin:</span>
                      <span className="detail-value">
                        {new Date(selectedMatch.opponentScheduleStartDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        {' - '}
                        {new Date(selectedMatch.opponentScheduleEndDate).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  {selectedMatch.pendingStatus === 'PENDING_WITHOUT_TIME' && selectedMatch.changedStartTime && selectedMatch.changedEndTime ? (
                    <>
                      {selectedMatch.changedDate && (
                        <div className="detail-item">
                          <span className="detail-label">Data:</span>
                          <span className="detail-value">{selectedMatch.changedDate}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">Godzina rozpoczęcia:</span>
                        <span className="detail-value">{selectedMatch.changedStartTime}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Godzina zakończenia:</span>
                        <span className="detail-value">{selectedMatch.changedEndTime}</span>
                      </div>
                      {selectedMatch.changedLocation && (
                        <div className="detail-item">
                          <span className="detail-label">Boisko:</span>
                          <span className="detail-value">{selectedMatch.changedLocation}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="detail-item">
                        <span className="detail-label">Data:</span>
                        <span className="detail-value">{selectedMatch.date || 'do ustalenia'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Godzina rozpoczęcia:</span>
                        <span className="detail-value">{selectedMatch.startTime || 'do ustalenia'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Godzina zakończenia:</span>
                        <span className="detail-value">{selectedMatch.endTime || 'do ustalenia'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Boisko:</span>
                        <span className="detail-value">
                          {selectedMatch.hasPitchAvailable
                            ? (selectedMatch.location || 'do ustalenia')
                            : 'do ustalenia'}
                        </span>
                      </div>
                    </>
                  )}
                  {selectedMatch.pendingStatus !== 'PENDING_WITH_DATE_TIME_CHANGE' && (
                    <div className="detail-item">
                      <span className="detail-label">Status:</span>
                      <span className={`status-badge ${getStatusClass(selectedMatch.status)}`}>
                        {getStatusLabel(selectedMatch.status)}
                      </span>
                    </div>
                  )}
                </div>

                {selectedMatch.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' && selectedMatch.changedStartTime && selectedMatch.changedEndTime && (
                  <div className="proposed-time-section">
                    <h4>Propozycja zmiany terminu</h4>
                    <div className="details-grid">
                      {selectedMatch.changedDate && (
                        <div className="detail-item">
                          <span className="detail-label">Proponowana data:</span>
                          <span className="detail-value">{selectedMatch.changedDate}</span>
                        </div>
                      )}
                      <div className="detail-item">
                        <span className="detail-label">Proponowana godzina rozpoczęcia:</span>
                        <span className="detail-value">{selectedMatch.changedStartTime}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Proponowana godzina zakończenia:</span>
                        <span className="detail-value">{selectedMatch.changedEndTime}</span>
                      </div>
                      {selectedMatch.changedLocation && (
                        <div className="detail-item">
                          <span className="detail-label">Proponowana lokalizacja:</span>
                          <span className="detail-value">{selectedMatch.changedLocation}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Wiadomości */}
              <div className="messages-section">
                <h4>Wiadomości</h4>
                {selectedMatch.messages && selectedMatch.messages.length > 0 ? (
                  <div className="messages-list">
                    {selectedMatch.messages.map((message) => {
                      // W SportsClubReceivedMatches zalogowany użytkownik jest opponentem (odbiorcą zapytania)
                      const isOwnMessage = message.senderId === selectedMatch.teamOpponentId;
                      const senderName = isOwnMessage
                        ? selectedMatch.sportsClubOpponentName
                        : selectedMatch.sportsClubCreatorName;
                      return (
                        <div
                          key={message.id}
                          className={`message-item ${isOwnMessage ? 'message-own' : 'message-other'}`}
                        >
                          <div className="message-content">
                            <div className="message-sender">{senderName}</div>
                            <div className="message-text">{message.message}</div>
                            {message.createdAt && (
                              <div className="message-timestamp">
                                {new Date(message.createdAt + (message.createdAt.endsWith('Z') ? '' : 'Z')).toLocaleString('pl-PL')}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="no-messages">Brak wiadomości</div>
                )}
              </div>

              {/* Sekcja odpowiedzi */}
              <div className="reply-section">
                <h4>Odpowiedz</h4>
                <div className="reply-form">
                  <textarea
                    className="reply-textarea"
                    placeholder="Wpisz swoją wiadomość..."
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    rows={3}
                    maxLength={800}
                  />
                  <button
                    className="reply-send-button"
                    onClick={handleSendReply}
                    disabled={sendingReply || !replyMessage.trim()}
                  >
                    {sendingReply ? 'Wysyłanie...' : 'Wyślij'}
                  </button>
                </div>
              </div>
            </div>
            {(selectedMatch.status === 'PENDING' || selectedMatch.status === 'PENDING_DATE_TIME_PROPOSAL' || selectedMatch.status === 'PENDING_CHANGE_DATE') ? (
              <div className="modal-footer">
                <button
                  className="modal-action-button approve-button"
                  onClick={handleApprove}
                  disabled={modalLoading}
                >
                  {modalLoading ? 'Przetwarzanie...' : 'Akceptuj'}
                </button>
                <button
                  className="modal-action-button reject-button"
                  onClick={() => setRejectConfirmOpen(true)}
                  disabled={modalLoading}
                >
                  {modalLoading ? 'Przetwarzanie...' : 'Odrzuć'}
                </button>
              </div>
            ) : (
              <div className="modal-footer-closed">
                <button
                  className="modal-action-button close-button"
                  onClick={closeModal}
                >
                  Zamknij
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal potwierdzenia odrzucenia */}
      {rejectConfirmOpen && (
        <div className="modal-overlay" onClick={() => setRejectConfirmOpen(false)}>
          <div className="reject-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reject-confirm-header">
              <h3>Potwierdzenie odrzucenia</h3>
            </div>
            <div className="reject-confirm-body">
              <p>{selectedMatch?.pendingStatus === 'PENDING_WITH_DATE_TIME_CHANGE' || selectedMatch?.pendingStatus === 'PENDING_WITHOUT_TIME'
                ? 'Czy na pewno chcesz odrzucić propozycję terminu rozegrania sparingu?'
                : 'Czy na pewno chcesz odrzucić to zapytanie o sparing?'}</p>
            </div>
            <div className="reject-confirm-footer">
              <button
                className="modal-action-button reject-button"
                onClick={() => {
                  setRejectConfirmOpen(false);
                  handleReject();
                }}
                disabled={modalLoading}
              >
                {modalLoading ? 'Przetwarzanie...' : 'Tak, odrzuć'}
              </button>
              <button
                className="modal-action-button close-button"
                onClick={() => setRejectConfirmOpen(false)}
              >
                Nie, wróć
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia blokowania kalendarza */}
      {pitchConfirmModal.isOpen && (
        <div className="modal-overlay" onClick={() => setPitchConfirmModal({ isOpen: false, match: null })}>
          <div className="reject-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reject-confirm-header">
              <h3>Potwierdzenie akceptacji</h3>
            </div>
            <div className="reject-confirm-body">
              <p>Czy chcesz rozegrać jeszcze jeden mecz tego dnia?</p>
            </div>
            <div className="reject-confirm-footer">
              <button
                className="modal-action-button approve-button"
                onClick={() => handleApproveWithCalendarDecision(false)}
                disabled={modalLoading}
              >
                {modalLoading ? 'Przetwarzanie...' : 'Tak'}
              </button>
              <button
                className="modal-action-button reject-button"
                onClick={() => handleApproveWithCalendarDecision(true)}
                disabled={modalLoading}
              >
                {modalLoading ? 'Przetwarzanie...' : 'Nie'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default SportsClubReceivedMatches;
